import React, { useEffect, useMemo, useRef, useState } from 'react';

import { reject as _reject, sortBy as _sortBy } from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import SineBrowserStorage from '@sine/sine-browser-storage';
import {
  Button,
  ButtonGroup,
  Form,
  Grid,
  Input,
  Loader,
  Modal,
  SingleSelect,
  TextArea,
} from '@sine/sine-ui-components';

import { promiseStatus } from '../../../helpers/async';
import formatSingularPlural from '../../../helpers/formatSingularPlural';
import mapToDropdownOptions from '../../../helpers/mapToDropdownOptions';
import parseServerError from '../../../helpers/parseServerError';
import securityPermissions from '../../../helpers/securityPermissions';
import notificationService from '../../../notifications/notificationService';
import invitationService from '../../../services/invitation/invitations.service';
import { useSitesWithPermissionsQuery } from '../../../services/site/site.hooks';
import visitService from '../../../services/visit/visit.service';
import zonesService from '../../../services/zones/zones.service';
import { useJoiForm } from '../../../shared/customHooks';
import { validateAndTransformObjectOfForms } from '../../../shared/customHooks/useJoiForm';
import { AclAction } from '../../../team/permission/constants/actions';
import { HIDE_CONFIRM_INVITATION_STORE_KEY } from '../invitations.constants';
import getDefaultZone from '../invitations.helpers';
import { generateCreateInvitationsSchema } from '../invitations.schemas';
import ConfirmInvitationModal from './ConfirmInvitationModal';
import DurationSelector from './DurationSelector';
import InviteesForm from './InviteesForm';

const StyledModal = styled(Modal)`
  div[class*='Modal__ModalOuterContainer'] {
    margin-top: auto;
    margin-bottom: auto;
  }

  div[data-st-id='modal-body'] {
    @media only screen and (min-width: 1200px) {
      height: 75vh;
    }
    overflow-y: auto;
  }
`;

export default function CreateInvitationModal({ show, onClose, invitation }) {
  const [isLoading, setIsLoading] = useState(true);
  const [siteRequirements, setSiteRequirements] = useState();
  const [visitorTypeOptions, setVisitorTypeOptions] = useState([]);
  const [hostOptions, setHostOptions] = useState([]);
  const [zoneOptions, setZoneOptions] = useState({ values: [], defaultOption: null });
  const [isHostRequired, setIsHostRequired] = useState(false);
  const [loadingSubmission, setLoadingSubmission] = useState(false);
  const [showConfirmInvitationModal, setShowConfirmInvitationModal] = useState(false);
  const [numberOfGuestsWithoutEmail, setNumberOfGuestsWithoutEmail] = useState(0);
  const [canSendEmail, setCanSendEmail] = useState(false);

  const isZonesEnabled = siteRequirements?.zonesEnabled && !!siteRequirements?.defaultZoneId;

  const { storage } = SineBrowserStorage.getInstance();

  const getStoredData = (storeKey) => JSON.parse(storage.getItem(storeKey));

  const schema = useMemo(
    () => generateCreateInvitationsSchema(isHostRequired, !!invitation, isZonesEnabled),
    [isHostRequired, isZonesEnabled],
  );

  const inviteesFormsRef = useRef({});

  const MAX_VISITOR_MESSAGE_CHAR_COUNT = 4000;
  const MAX_ADMIN_MESSAGE_CHAR_COUNT = 1000;

  const form = useJoiForm(schema, {
    companyName: invitation?.companyDetails?.name || undefined,
    subject: invitation?.subject || '',
    adminNotes: invitation?.adminNotes || '',
    siteOption: undefined,
    zone: invitation?.zone || undefined,
    visitorTypeOption: undefined,
    expiry: invitation ? invitation.expiry : {},
    hostOption: undefined,
    validFrom: invitation ? new Date(invitation?.startDate) : moment().add(1, 'hour').startOf('hour').toDate(),
    validTo: invitation ? new Date(invitation?.endDate) : new Date(),
    visitorNotes: invitation?.visitorNotes || '',
  });

  const { sites, isLoadingSites } = useSitesWithPermissionsQuery([AclAction.SIGN_IN, AclAction.H_PROFILE_PASS]);
  const { allSites, siteOptions } = useMemo(() => {
    const _allSites = sites.filter(
      (site) => site.settings.hostRequiredForSignIn || site.permissions.includes(AclAction.SIGN_IN),
    );

    const _siteOptions = _allSites.map((site) => ({
      value: site.id,
      label: site.name,
      countryCode: site.countryCode,
    }));

    return {
      allSites: _allSites,
      siteOptions: _siteOptions,
    };
  }, [sites]);

  // Set site options & value
  useEffect(() => {
    if (invitation) {
      form.onChange(
        'siteOption',
        siteOptions.find((site) => invitation.site === site.value),
      );
    } else {
      form.onChange('siteOption', siteOptions[0]);
    }
  }, [siteOptions]);

  // Get site requirements
  useEffect(() => {
    if (form.values.siteOption) {
      (async () => {
        setIsLoading(true);
        const selectedSiteId = form.values.siteOption.value;

        const _siteRequirements = await visitService.requirements(selectedSiteId);

        setSiteRequirements(_siteRequirements);

        const site = allSites.find((site2) => site2.id === selectedSiteId);

        const siteVisitorTypes = _reject(_siteRequirements.visitorTypes, (visitorType) => {
          return (
            !securityPermissions.checkPermissions(site.permissions, 'SIGN_IN') &&
            !site.settings?.hostByVisitorTypeRequiredForSignIn.includes(visitorType.id)
          );
        });

        const _mappedVisitorTypes = mapToDropdownOptions(siteVisitorTypes);

        setVisitorTypeOptions(_mappedVisitorTypes);
        setIsLoading(false);
      })();
    }
  }, [form.values.siteOption]);

  // Set visitor type
  useEffect(() => {
    if (invitation) {
      const invitationVisitorType = visitorTypeOptions.find(
        (visitorType) => visitorType.value === invitation.visitorType,
      );
      form.onChange('visitorTypeOption', invitationVisitorType || visitorTypeOptions[0]);
    } else {
      form.onChange('visitorTypeOption', visitorTypeOptions[0]);
    }
  }, [visitorTypeOptions]);

  // HGxVT
  const { activeHostGroupIdExclusions, activeHostExclusions } = useMemo(() => {
    const _activeHostGroupIdExclusions = (siteRequirements?.hostGroupVisitorTypeExclusions || [])
      .filter((exclusion) => exclusion.visitorTypeId === form.values.visitorTypeOption?.value)
      .map((exclusion) => exclusion.hostGroupId);

    const _activeHostExclusions = (siteRequirements?.hostGroups || [])
      .filter((hostGroup) => _activeHostGroupIdExclusions.includes(hostGroup?.id))
      .flatMap((hostGroup) => hostGroup.hosts.map((host) => host.id));

    return { activeHostGroupIdExclusions: _activeHostGroupIdExclusions, activeHostExclusions: _activeHostExclusions };
  }, [
    siteRequirements?.hostGroupVisitorTypeExclusions,
    form.values.visitorTypeOption?.value,
    siteRequirements?.hostGroups,
  ]);

  // Get host requirements & options
  useEffect(() => {
    const _isHostRequired = (siteRequirements?.hostByVisitorTypeRequired || []).includes(
      form.values.visitorTypeOption?.value,
    );

    const _hostOptions = (siteRequirements?.hostGroups || [])
      .filter((hostGroup) => !activeHostGroupIdExclusions.includes(hostGroup.id))
      .flatMap((hostGroup) =>
        hostGroup.hosts.map((host) => ({
          label: `${host.firstName} ${host.lastName} (${hostGroup.name})`,
          value: host.id,
        })),
      );

    const _sortedHostOptions = _sortBy(_hostOptions, 'label');

    setIsHostRequired(_isHostRequired);
    setHostOptions(_sortedHostOptions);
  }, [
    form.values.visitorTypeOption,
    form.values.siteOption,
    activeHostGroupIdExclusions,
    siteRequirements?.hostByVisitorTypeRequired,
    siteRequirements?.hostGroups,
  ]);

  useEffect(() => {
    const selectedVisitorType = form.values.visitorTypeOption?.value;
    const showHost =
      !!selectedVisitorType && siteRequirements?.hostByVisitorTypeRequired?.includes(selectedVisitorType.value);

    const selectedHostOption = form?.values?.host?.value;
    const noHostSelected = showHost && !selectedHostOption;

    const shouldResetSelectedHost = activeHostExclusions.includes(selectedHostOption) || noHostSelected;
    if (shouldResetSelectedHost) {
      form.onChange('host', hostOptions[0] || null);
    }
  }, [form?.values?.host?.value]);

  // Set Host
  useEffect(() => {
    (async () => {
      if (isHostRequired) {
        if (invitation) {
          const invitationHost = hostOptions.find((_host) => _host.value === invitation.host);

          const currentHost = hostOptions.find((_host) => _host.value === form.values.hostOption?.value);

          !currentHost && form.onChange('hostOption', invitationHost || currentHost || hostOptions[0]);
        } else {
          const currentSelection = await hostOptions.find((_host) => _host.value === form.values.hostOption?.value);

          !currentSelection && form.onChange('hostOption', hostOptions[0]);
        }
      } else {
        form.onChange('hostOption', undefined);
        form.onChange('zone', undefined);
      }
    })();
  }, [hostOptions]);

  // Get zones
  useEffect(() => {
    if (form.values.hostOption && form.values.siteOption && isZonesEnabled) {
      (async () => {
        try {
          setIsLoading(true);
          const zonesForHostProfile = await zonesService.getZonesForHostProfile(
            form.values.siteOption.value,
            form.values.hostOption.value,
          );

          const invitationZone = zonesForHostProfile.find((zone) => zone.id === invitation?.zoneId);
          const defaultZoneForHostGroup = zonesForHostProfile.find((zone) =>
            zone.hostGroups?.find((zoneHostGroup) => zoneHostGroup.default),
          );
          const defaultZoneForSite = zonesForHostProfile.find((zone) => zone.default);

          const defaultZone = getDefaultZone(
            zoneOptions.values,
            invitationZone,
            defaultZoneForHostGroup,
            defaultZoneForSite,
          );

          setZoneOptions({
            values: mapToDropdownOptions(zonesForHostProfile),
            defaultOption: { label: defaultZone?.name, value: defaultZone?.id },
          });
        } catch (error) {
          notificationService.addNotification({
            message: parseServerError(error),
            variant: 'danger',
          });
        } finally {
          setIsLoading(false);
        }
      })();
    } else {
      setZoneOptions({ values: [], defaultOption: null });
      form.onChange('zone', undefined);
    }
  }, [form.values.hostOption, hostOptions]);

  useEffect(() => {
    const invitationZone = invitation?.zoneDetails;

    if (invitationZone) {
      const selectedZone = zoneOptions.values.find((zone) => zone.value === invitationZone?.id);

      if (selectedZone && form.values?.hostOption?.value === invitation?.host) {
        form.onChange('zone', selectedZone);
      } else {
        form.onChange('zone', zoneOptions.values.length ? zoneOptions.defaultOption : undefined);
      }
    } else {
      form.onChange('zone', zoneOptions.values.length ? zoneOptions.defaultOption : undefined);
    }
  }, [zoneOptions, invitation]);

  const formatNotesMessage = (limit, value) => {
    if (limit - value >= 0) {
      return `${formatSingularPlural(limit - value, 'character', 'characters')} left`;
    } else {
      return `${formatSingularPlural(
        value - limit,
        'character',
        'characters',
      )} over the limit. Please reduce your message`;
    }
  };

  const onShowConfirmInvitationModal = () => {
    // Run both validations to triggers error before testing validity.
    const invitees = validateAndTransformObjectOfForms(inviteesFormsRef.current);
    const isSharedValuesValid = form.validate();

    if (invitees.isValid && isSharedValuesValid) {
      setShowConfirmInvitationModal(true);
    }
  };

  const checkInvitationDetails = (invitees) => {
    const {
      companyName,
      adminNotes,
      visitorNotes,
      expiry,
      siteOption,
      hostOption,
      visitorTypeOption,
      validFrom,
      zone,
      validTo,
      subject,
    } = form.validateAndTransform().values;

    let formattedExpiry;

    if (expiry.type === 'elapsed' && typeof expiry.settings.unit === 'object') {
      formattedExpiry = {
        type: expiry.type,
        settings: {
          unit: expiry.settings.unit.label.toLowerCase(),
          value: expiry.settings.value,
        },
      };
    } else if (expiry.type === 'specific' && typeof expiry.settings === 'object') {
      formattedExpiry = {
        type: expiry.type,
      };
    } else {
      formattedExpiry = expiry;
    }

    const invitationDetails = {
      company: companyName,
      companyDetails: { name: companyName },
      expiry: formattedExpiry,
      siteDetails: siteOption.site,
      hostId: hostOption?.value,
      subject,
      visitorType: visitorTypeOption.value,
      adminNotes: adminNotes,
      visitorNotes: visitorNotes,
      siteId: siteOption.value,
      zoneId: isZonesEnabled && zoneOptions.values.length ? zone.value : undefined,
      zoneDetails: isZonesEnabled && zoneOptions.values.length ? zone : undefined,
      startDate: new Date(validFrom).toISOString(),
      endDate: new Date(validTo).toISOString(),
    };

    return invitees.values.map((invitee) => ({ ...invitee, ...invitationDetails, id: invitation?.id }));
  };

  const onCreateInvitations = async () => {
    // Run both validations to triggers error before testing validity.
    const invitees = validateAndTransformObjectOfForms(inviteesFormsRef.current);
    const isSharedValuesValid = form.validate();

    if (invitees.isValid && isSharedValuesValid) {
      const _invitations = checkInvitationDetails(invitees);
      const _invitationsPluralSingular = formatSingularPlural(_invitations.length, 'invitation', 'invitations', true);

      try {
        setLoadingSubmission(true);
        if (invitation) {
          _invitations[0].id = invitation.id;
        }

        notificationService.addNotification({
          message: `Processing your ${_invitationsPluralSingular}`,
          variant: 'info',
        });

        const createInvitationsResponse = await invitationService.createMultipleInvitations(_invitations, canSendEmail);

        notificationService.clearNotifications();

        const createdInvitations = createInvitationsResponse.filter(
          (_invitation) => _invitation.status !== promiseStatus.rejected,
        );
        const blockedInvitationsByChecklist = createInvitationsResponse.filter(
          (_invitation) => _invitation.status === promiseStatus.rejected,
        );

        const _createdInvitationsPluralSingular = formatSingularPlural(
          createdInvitations.length,
          'invitation',
          'invitations',
          true,
        );

        const _createdMessagePluralSingular = formatSingularPlural(createdInvitations.length, 'was', 'were', true);

        if (createdInvitations.length >= 1) {
          notificationService.addNotification({
            heading: `${createdInvitations.length > 1 ? 'Invitations' : 'Invitation'} created`,
            message:
              !invitation &&
              `${createdInvitations.length} ${_createdInvitationsPluralSingular} ${_createdMessagePluralSingular} successfully created`,
            variant: 'success',
          });

          onClose();
        }

        if (blockedInvitationsByChecklist.length) {
          const blockedInvitationsByChecklistError = {
            message: blockedInvitationsByChecklist[0].reason,
            data: { code: 'INVALID_NOT_PERMITTED', length: blockedInvitationsByChecklist.length },
          };

          throw blockedInvitationsByChecklistError;
        }
      } catch (error) {
        let heading = `Error creating ${_invitationsPluralSingular}`;
        let message = parseServerError(error);
        let shouldCloseModal = true;

        const _blockedInvitationsPluralSingular = formatSingularPlural(
          error.data?.length,
          'invitation',
          'invitations',
          true,
        );

        switch (error.data?.code) {
          case 'INVALID_INVITATION':
            error.customMsg = `Your invitation start and end date exceeds the 5 year limit. Please adjust the ${_invitationsPluralSingular} to fit within this limit and try again.`;
            shouldCloseModal = false;
            break;

          case 'INVALID_NOT_PERMITTED':
            heading = `Blocked ${_blockedInvitationsPluralSingular}`;
            message = `${error.data.length} ${_blockedInvitationsPluralSingular} blocked. Check your email for details`;
            shouldCloseModal = true;
            break;

          default:
            shouldCloseModal = false;
            break;
        }

        notificationService.addNotification({
          heading,
          message,
          variant: 'danger',
        });

        setLoadingSubmission(false);

        shouldCloseModal && onClose();
      }
    }
  };

  const onUpdateInvitation = async () => {
    // Run both validations to triggers error before testing validity.
    const invitees = validateAndTransformObjectOfForms(inviteesFormsRef.current);
    const isSharedValuesValid = form.validate();

    if (invitees.isValid && isSharedValuesValid) {
      const _invitations = checkInvitationDetails(invitees);

      try {
        setLoadingSubmission(true);
        if (invitation) {
          _invitations[0].id = invitation.id;
        }

        await invitationService.updateInvitation(_invitations[0], canSendEmail);

        notificationService.addNotification({
          heading: 'Invitation edited',
          variant: 'success',
        });

        onClose();
      } catch (error) {
        let heading;
        let shouldCloseModal = true;

        switch (error.data.code) {
          case 'INVALID_INVITATION':
            error.customMsg = `Your invitation start and end date exceeds the 5 year limit. Please adjust the invitation to fit within this limit and try again.`;
            shouldCloseModal = false;
            break;

          case 'INVITATION_NOT_PERMITTED':
            heading = 'Unable to edit the invitation';
            error.customMsg = 'Your changes to the invitation were blocked. Check your email for details.';
            shouldCloseModal = true;
            break;

          default:
            heading = 'Error updating invitation';
            shouldCloseModal = false;
            break;
        }

        notificationService.addNotification({
          heading: heading,
          message: parseServerError(error),
          variant: 'danger',
        });

        shouldCloseModal && onClose();
      } finally {
        setLoadingSubmission(false);
      }
    }
  };

  const loading = isLoading || isLoadingSites;

  return (
    <StyledModal
      show={show}
      size='xl'
      onClose={onClose}
      header={invitation ? 'Edit Invitation' : 'Create Invitation'}
      footer={
        <ButtonGroup alignment='right'>
          <Button variant='tertiary' onClick={onClose} data-st-ext='button-cancel'>
            Cancel
          </Button>
          <Button
            variant={loadingSubmission ? 'disabled' : 'primary'}
            onClick={
              canSendEmail && numberOfGuestsWithoutEmail > 0 && !getStoredData(HIDE_CONFIRM_INVITATION_STORE_KEY)
                ? onShowConfirmInvitationModal
                : !invitation
                ? onCreateInvitations
                : onUpdateInvitation
            }
            loading={loadingSubmission}
            data-st-ext='button-create'
            children={invitation ? `Save invitation` : `Create invitation`}
          />
        </ButtonGroup>
      }
    >
      <Loader overlay show={loading} />

      <Form values={form.values} errors={form.errors} onFieldChange={form.onChange}>
        <Grid container>
          <Grid alignItems='stretch'>
            <InviteesForm
              invitation={invitation}
              allowMultiple={!invitation}
              formsRef={inviteesFormsRef}
              selectedSite={form.values.siteOption}
              isLoading={loading}
              canSendEmail={canSendEmail}
              setCanSendEmail={setCanSendEmail}
              setNumberOfGuestsWithoutEmail={setNumberOfGuestsWithoutEmail}
            />
          </Grid>

          <Grid md={6} alignItems='stretch'>
            <Input label='Company' name='companyName' data-st-ext='input-company' optional />
          </Grid>
          <Grid md={6} alignItems='stretch'>
            <Input name='subject' label='Invite Subject' data-st-ext='invite-subject' noSpaceReservation optional />
          </Grid>

          <Grid alignItems='stretch'>
            <TextArea
              name='adminNotes'
              label='Notes'
              rows={2}
              message={`${formatNotesMessage(
                MAX_ADMIN_MESSAGE_CHAR_COUNT,
                form.values.adminNotes?.length,
              )} (Only visible to admins)`}
              data-st-ext='admin-notes'
              noSpaceReservation
              optional
            />
          </Grid>
          <Grid md={isHostRequired ? 4 : 6} alignItems='stretch'>
            <SingleSelect
              name='siteOption'
              label='Site'
              options={siteOptions}
              data-st-ext='select-site'
              noSpaceReservation
            />{' '}
          </Grid>
          <Grid md={isHostRequired ? 4 : 6} alignItems='stretch'>
            <SingleSelect
              name='visitorTypeOption'
              label='Visitor Type'
              options={visitorTypeOptions}
              disabled={!visitorTypeOptions.length}
              data-st-ext='select-visitor-type'
              noSpaceReservation
              disableSearch
            />
          </Grid>
          {isHostRequired && (
            <Grid md={4} alignItems='stretch'>
              <SingleSelect
                name='hostOption'
                label='Host'
                options={hostOptions}
                data-st-ext='select-host'
                noSpaceReservation
              />
            </Grid>
          )}

          {isZonesEnabled && isHostRequired && !!zoneOptions.values.length && (
            <Grid xs={4} alignItems='stretch'>
              <SingleSelect name='zone' label='Zone' options={zoneOptions.values} data-st-ext='select-zone' />
            </Grid>
          )}
          <Grid spacing={0} alignItems='stretch'>
            <DurationSelector form={form} invitation={invitation} />
          </Grid>

          <Grid alignItems='stretch'>
            <TextArea
              name='visitorNotes'
              label='Message to Guest'
              rows={2}
              message={formatNotesMessage(MAX_VISITOR_MESSAGE_CHAR_COUNT, form.values.visitorNotes?.length)}
              data-st-ext='text-area-visitor-message'
              optional
            />
          </Grid>
        </Grid>
      </Form>

      {showConfirmInvitationModal && (
        <ConfirmInvitationModal
          onClose={() => setShowConfirmInvitationModal(false)}
          onCreateInvitations={!invitation ? onCreateInvitations : onUpdateInvitation}
          numberOfGuestsWithoutEmail={numberOfGuestsWithoutEmail}
          loadingSubmission={loadingSubmission}
        />
      )}
    </StyledModal>
  );
}

CreateInvitationModal.propTypes = {
  onClose: PropTypes.func,
  show: PropTypes.bool,
  invitation: PropTypes.object,
};
